import React, { FC, useState } from "react";
import styled from "styled-components";
import InputText from "components/corphurusato/InputText";
import Section from "components/corphurusato/parts/Section";
import Headline, { Big } from "components/corphurusato/parts/Headline";
import SubHeadline from "components/corphurusato/parts/SubHeadline";
import ResultArea, { Result } from "components/corphurusato/ResultArea";
import { FrontCorphurusatoCalcService } from "lib/service/client/FrontCorphurusatoCalcService";
import SeeMoreButton from "components/corphurusato/parts/SeeMoreButton";
import Content from "components/corphurusato/parts/Content";
import BottomSlide from "components/corphurusato/parts/BottomSlide";
import { Formik } from "formik";
import { number, object } from "yup";

const SimulatorSection: FC = () => {
  const initialValue: Form = { 所得: "", 資本金: "", 寄付金: "" };
  const [result, setResult] = useState<Result>();
  const validationSchema = object().shape({
    所得: amountRule.required("必須です"),
    資本金: amountRule.required("必須です"),
    寄付金: amountRule.optional(),
  });
  const calculate = async (values: Form) => {
    if (!values["所得"] || !values["資本金"]) return;
    const service = new FrontCorphurusatoCalcService();
    const result = await service.corphurusatoTax({ 所得: values["所得"], 資本金: values["資本金"], 寄付金: values["寄付金"] || null });
    if (result.isSuccess()) {
      setResult(result.value);
    } else {
      // TODO: どう対処するか要検討
    }
  };
  const [showsDetail, setShowsDetail] = useState(false);
  return (
    <Section>
      <Headline color={"#FBB42B"}>
        <Big>寄附金額</Big>シミュレーター
      </Headline>
      <Content>
        <Formik initialValues={initialValue} validationSchema={validationSchema} onSubmit={calculate}>
          {({ handleSubmit, isValid }) => (
            <Simulator>
              <InputArea>
                <div>
                  <Label>所得金額</Label>
                  <Required>【必須】</Required>
                </div>
                <StyledInputText name="所得" />
              </InputArea>
              <InputArea>
                <div>
                  <Label>資本金</Label>
                  <Required>【必須】</Required>
                </div>
                <StyledInputText name="資本金" />
              </InputArea>
              <InputArea>
                <Label>寄附予定金額</Label>
                <StyledInputText name="寄付金" />
              </InputArea>
              <ButtonArea>
                <CalculationButton type="submit" onClick={() => handleSubmit()} disabled={!isValid}>
                  計算結果
                </CalculationButton>
              </ButtonArea>
            </Simulator>
          )}
        </Formik>
        {result && <ResultArea result={result} />}
        <Attention>
          <AttentionTitle>【注意事項】</AttentionTitle>
          <Paragraph>※あくまでもシミュレーターであることをご了承ください。</Paragraph>
          <Paragraph>※最低寄附金額は10万円となります。</Paragraph>
          <Paragraph>※寄附予定額を省略した場合、最適な寄附額を計算します。</Paragraph>
          <Paragraph>※資本金1億円超の場合「法人事業税に外形標準」を適用します。</Paragraph>
          <Paragraph>※資本金1億円超の場合「法人住民税に超過税率」を適用します。</Paragraph>
        </Attention>
      </Content>
      {!showsDetail && (
        <SeeMoreButtonArea>
          <StyledSeeMoreButton
            label="仕組みをもっと見る"
            color={"#FBB42B"}
            arrowImage="/image/lp/corphurusato/simulator/doun_over.png"
            onClick={() => setShowsDetail(true)}
          />
        </SeeMoreButtonArea>
      )}
      <BottomSlide in={showsDetail}>
        <StyledContent>
          <Line />
          <Headline color={"#FBB42B"}>
            企業版ふるさと納税の<Big>仕組み</Big>
          </Headline>
          <Paragraph>
            企業版ふるさと納税とは、最大で約9割を税額軽減する仕組みです。
            <br />
            税額軽減は、「約6割の税額控除」と「約3割の損金算入」の合計です。
          </Paragraph>
          <StyledSubHeadline label="企業版ふるさと納税の税控除の内訳" color={"#FBB42B"} />
          <Image src="/image/lp/corphurusato/simulator/figure_04.png" alt="企業版ふるさと納税の税控除の内訳" />
          <StyledSubHeadline label="企業版ふるさと納税の税控除の内訳" color={"#FBB42B"} />
          <Image src="/image/lp/corphurusato/simulator/figure_05.png" alt="企業版ふるさと納税の税控除の内訳" />
        </StyledContent>
      </BottomSlide>
    </Section>
  );
};
export default SimulatorSection;

const StyledContent = styled(Content)`
  padding-top: 80px
`;

const Line = styled.hr`
  margin-top: 0;
  margin-bottom: 30px;
  border-top: dotted 6px #999;
  border-bottom: none;
  border-right: none;
  border-left: none;
`;

const SeeMoreButtonArea = styled.div`
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const StyledSeeMoreButton = styled(SeeMoreButton)``;

const StyledSubHeadline = styled(SubHeadline)`
  @media screen and (max-width: 960px) {
    margin-top: 20px;
  }

  @media screen and (min-width: 961px) {
    margin-top: 30px;
  }
`;

const Simulator = styled.div`
  margin: 0 auto;
  background-color: #fff7ea;

  @media screen and (max-width: 960px) {
    padding: 20px;
    width: 100%;
    max-width: 700px;
  }
  @media screen and (min-width: 961px) {
    padding: 20px 40px;
    width: 700px;
  }
`;

const InputArea = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:not(:first-child) {
    margin-top: 20px;
  }
`;

const Label = styled.label`
  font-weight: bold;

  @media screen and (max-width: 960px) {
    font-size: 16px;
  }

  @media screen and (min-width: 961px) {
    font-size: 20px;
  }
`;

const Required = styled.span`
  color: red;
  @media screen and (max-width: 960px) {
    font-size: 12px;
  }

  @media screen and (min-width: 961px) {
    font-size: 14px;
  }
`;

const Attention = styled.div`
  border: 2px solid #d6d6d6;
  padding: 20px 18px;

  @media screen and (max-width: 960px) {
    margin: 10px 0;
    width: 100%;
    max-width: 700px;
  }

  @media screen and (min-width: 961px) {
    margin: 20px auto;
    width: 700px;
  }
`;

const AttentionTitle = styled.h3`
  font-weight: bold;
  text-align: center;
  font-size: 1.26em;
  margin-bottom: 10px;
`;

const Paragraph = styled.p`
  line-height: 2em;
`;

const Image = styled.img`
  @media screen and (max-width: 960px) {
    margin: 16px 0;
    width: 100%;
  }

  @media screen and (min-width: 961px) {
    margin: 30px 10%;
    width: 80%;
  }
`;

const ButtonArea = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-top: 30px;
  grid-row-gap: 20px;
  row-gap: 20px;
`;

const StyledInputText = styled(InputText)`
  @media screen and (max-width: 960px) {
    flex-basis: 60%;
  }

  @media screen and (min-width: 961px) {
    flex-basis: 60%;
  }
`;

const CalculationButton = styled.button`
  background: #fe0100;
  border-radius: 2em;
  color: #fff;
  font-family: sans-serif;
  font-size: 1.24em;
  font-weight: bold;
  font-style: italic;
  display: block;
  padding: 0.8em 4em;
`;

const amountRule = number()
  .transform((o, v) => parseFloat(v?.replace(/,/g, "") || ""))
  .lessThan(10000000000, "申し訳ありません。100億円以上は対応しておりません。");

interface Form {
  所得: number | "";
  資本金: number | "";
  寄付金: number | "";
}
